import * as React from "react";
import { Header, Form, Divider, Label, Popup, Icon, Grid } from "semantic-ui-react";
import { createValidator } from "not-valid";

import { Input } from "@neworbit/simpleui-input";
import { ValidationObject } from "@neworbit/simpleui-forms";

import {
    MedicalType,
    MedicalOutcomeModel,
    MedicalOutcome,
    MedicalOutcomes,
    DecisionPendingClarificationOptions
} from "@teamprevent/common/healthSurveillance/models";
import { TextSnippetSelector } from "@teamprevent/common/components/TextSnippetSelector";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { MedicalOutcomeFormModel } from "../models";

import { MedicalOutcomeFormOld } from "./MedicalOutcomeFormOld";

interface Props {
    medicalType: MedicalType;
    model: MedicalOutcomeFormModel;
    showErrors: boolean;
    onChange: (model: MedicalOutcomeFormModel) => void;
}

export const MedicalOutcomeForm: React.FunctionComponent<Props> = (props) => {
    const enableHealthSurveillanceCrs11_3 = getFeatureToggles()[FeatureToggleNames.EnableHealthSurveillanceCrs11_3];
    if (enableHealthSurveillanceCrs11_3) {
        return <MedicalOutcomeFormNew {...props} />;
    } else {
        return <MedicalOutcomeFormOld {...props} />;
    }
};

const MedicalOutcomeFormNew: React.FunctionComponent<Props> = ({ medicalType, model, showErrors, onChange }) => {
    const [valid, setValid] = React.useState<ValidationObject<MedicalOutcomeModel>>({ outcome: false });

    const areClinicalNotesMandatoryForOutcome = (outcome: MedicalOutcome) => [
        MedicalOutcome.decisionPending,
        MedicalOutcome.issue,
        MedicalOutcome.fitWithAdjustments,
        MedicalOutcome.unfit
    ].includes(outcome);

    const areCustomerNotesMandatoryForOutcome = (outcome: MedicalOutcome) => [
        MedicalOutcome.issue,
        MedicalOutcome.fitWithAdjustments,
        MedicalOutcome.unfit,
    ].includes(outcome);

    const isRecallRequiredForOutcome = (outcome: MedicalOutcome) =>
        [MedicalOutcome.issue, MedicalOutcome.noIssue, MedicalOutcome.fitWithAdjustments, MedicalOutcome.fit].includes(outcome);

    const validateClinicalNotes = React.useCallback(() =>
        model.values.clinicalNotes.trim() !== ""
        || !areClinicalNotesMandatoryForOutcome(model.values.outcome),
    [model.values.clinicalNotes, model.values.outcome]);

    const validateCustomerNotes =  React.useCallback(() =>
        model.values.customerNotes?.trim() !== ""
        || !areCustomerNotesMandatoryForOutcome(model.values.outcome),
    [model.values.customerNotes, model.values.outcome]);

    const validateRecallYearsAndWeeks = React.useCallback(() =>
        !isRecallRequiredForOutcome(model.values.outcome)
        || model.values.recallYears > 0
        || model.values.recallWeeks > 0,
    [model.values.outcome, model.values.recallWeeks, model.values.recallYears]);

    const validate = React.useCallback((): boolean => {
        return validateClinicalNotes() &&
            validateCustomerNotes() &&
            validateRecallYearsAndWeeks() &&
            Object.values(valid).every(v => v);
    }, [valid, validateClinicalNotes, validateCustomerNotes, validateRecallYearsAndWeeks]);

    const setCustomerNotes = React.useCallback((value: string, valueValid: boolean) => {
        onChange({ values: { ...model.values, customerNotes: value }, valid: validate() });
        setValid({ ...valid, customerNotes: valueValid });
    }, [model.values, onChange, valid, validate]);

    const updateCustomerNotes = React.useCallback((snippetText: string) => {
        setCustomerNotes((model.values.customerNotes ? model.values.customerNotes + " " : "") + snippetText, validateCustomerNotes());
    }, [model.values.customerNotes, setCustomerNotes, validateCustomerNotes]);

    React.useEffect(() => onChange({ ...model, valid: validate() }), [valid]);

    const onCategoryChange = React.useCallback((value: string, valueValid: boolean) => {
        onChange({
            values: {
                ...model.values,
                outcome: value as MedicalOutcome,
                recallYears: isRecallRequiredForOutcome(value as MedicalOutcome) ? model.values.recallYears : null,
                recallWeeks: isRecallRequiredForOutcome(value as MedicalOutcome) ? model.values.recallWeeks : null
            }, valid: validate()
        });
        setValid({
            ...valid,
            outcome: valueValid,
            recallYears: valid.recallYears || !isRecallRequiredForOutcome(value as MedicalOutcome),
            recallWeeks: valid.recallWeeks || !isRecallRequiredForOutcome(value as MedicalOutcome),
            clinicalNotes: valid.clinicalNotes || value === MedicalOutcome.noIssue,
            customerNotes: valid.customerNotes || value === MedicalOutcome.noIssue
        });
    }, [model.values, onChange, valid, validate]);

    const onClinicalNotesChange = React.useCallback((value, valueValid) => {
        onChange({ values: { ...model.values, clinicalNotes: value }, valid: validate() });
        setValid({ ...valid, clinicalNotes: valueValid });
    }, [model.values, onChange, valid, validate]);

    const onDecisionPendingClarificationChange = React.useCallback((value, valueValid) => {
        onChange({ values: { ...model.values, decisionPendingClarification: value }, valid: validate() });
        setValid({ ...valid, decisionPendingClarification: valueValid });
    }, [model.values, onChange, valid, validate]);

    const onRecallYearsChange = React.useCallback((value, valueValid) => {
        onChange({ values: { ...model.values, recallYears: value }, valid: validate() });
        setValid({ ...valid, recallYears: valueValid });
    }, [model.values, onChange, valid, validate]);

    const onRecallWeeksChange = React.useCallback((value, valueValid) => {
        onChange({ values: { ...model.values, recallWeeks: value }, valid: validate() });
        setValid({ ...valid, recallWeeks: valueValid });
    }, [model.values, onChange, valid, validate]);

    return (
        <>
            <Header content={medicalType.name} />
            <Form.Field>
                <Grid><Grid.Row>
                    <Grid.Column width={model.values.outcome === MedicalOutcome.unfit ? "15" : "16"}>
                        <Input.Dropdown
                            dynamicOptions
                            options={(MedicalOutcomes[medicalType.category])
                                .map(o => ({ text: o, value: o }))}
                            value={model.values.outcome}
                            showErrors={showErrors}
                            required
                            onChange={onCategoryChange}
                        />
                    </Grid.Column>
                    {model.values.outcome === MedicalOutcome.unfit &&
                        <Grid.Column width="1" verticalAlign="middle">
                            <Popup
                                content="This will mark the employee as permanently unfit and no recall will be created.
                                    This decision needs to be made by an OHP."
                                trigger={<Icon color="red" name="warning sign" />}
                            />
                        </Grid.Column>
                    }
                </Grid.Row></Grid>
            </Form.Field>
            <Form.Field>
                <Input.Textarea
                    label="Clinical Notes"
                    value={model.values.clinicalNotes}
                    showErrors={showErrors}
                    onChange={onClinicalNotesChange}
                />
            </Form.Field>
            {
                showErrors && !validateClinicalNotes() && <Label basic color="red" pointing>You must enter clinical notes</Label>
            }
            {
                model.values.outcome === MedicalOutcome.decisionPending && <Form.Field>
                    <Input.Dropdown
                        label="Decision Pending Clarification - this wording will appear on the certificate"
                        dynamicOptions
                        options={DecisionPendingClarificationOptions}
                        value={model.values.decisionPendingClarification}
                        showErrors={showErrors}
                        required
                        onChange={onDecisionPendingClarificationChange}
                    />
                </Form.Field>
            }
            <div className="text-snippet-container-padded">
                <TextSnippetSelector onSelect={updateCustomerNotes} />
            </div>
            <Form.Field>
                <Input.Textarea
                    label="Customer Notes"
                    value={model.values.customerNotes}
                    showErrors={showErrors}
                    onChange={setCustomerNotes}
                />
            </Form.Field>
            {
                showErrors && !validateCustomerNotes() && <Label basic color="red" pointing>You must enter customer notes</Label>
            }
            {isRecallRequiredForOutcome(model.values.outcome) &&
                <>
                    <Form.Field>
                        <Input.Number
                            label="Recall (years)"
                            value={model.values.recallYears}
                            showErrors={showErrors}
                            required
                            validation={[
                                createValidator(value => value >= 0 && value <= 40, "Must be between 0 and 40 years"),
                                createValidator(Number.isInteger, "Must be a whole number")
                            ]}
                            onChange={onRecallYearsChange}
                        />
                    </Form.Field>
                    <Form.Field>
                        <Input.Number
                            label="Recall (weeks)"
                            value={model.values.recallWeeks}
                            showErrors={showErrors}
                            required
                            validation={[
                                createValidator(value => value >= 0 && value <= 51, "Must be between 0 and 51 weeks"),
                                createValidator(Number.isInteger, "Must be a whole number")
                            ]}
                            onChange={onRecallWeeksChange}
                        />
                    </Form.Field>
                    {
                        showErrors && !validateRecallYearsAndWeeks() &&
                    <Label basic color="red" pointing>Recall years or weeks must be set to a value more than 0</Label>
                    }
                </>
            }
            <Divider />
        </>
    );
};
